import type { ReactNode } from 'react'

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faArrowRightArrowLeft,
  faClock,
  faCreditCard,
  faMoneyCheckDollar,
} from '@fortawesome/pro-regular-svg-icons'

export type AvailablePaymentMethodsRenderTextMap = {
  title: ReactNode
  introDisclaimer: ReactNode
  onlineTitle: ReactNode
  onlineDesc: ReactNode
  /** Required when `card` is true. Omit when not rendering the Credit Card row. */
  cardTitle?: ReactNode
  /** Required when `card` is true. Omit when not rendering the Credit Card row. */
  cardDesc?: ReactNode
  /** Required when `ach` is true. Omit when not rendering the ACH Bank Transfer row. */
  achTitle?: ReactNode
  /** Required when `ach` is true. Omit when not rendering the ACH Bank Transfer row. */
  achDesc?: ReactNode
  /** Required when `netTerms` is true. Omit when not rendering the Net Terms row. */
  netTermsTitle?: ReactNode
  /** Required when `netTerms` is true. Omit when not rendering the Net Terms row. */
  netTermsDesc?: ReactNode
  /** Required when `wire` is true. Omit when not rendering the Wire Transfer row. */
  wireTitle?: ReactNode
  /** Required when `wire` is true. Omit when not rendering the Wire Transfer row. */
  wireDesc?: ReactNode
  /** Short label for the collapsed view's chip. Falls back to `cardTitle`. */
  cardChip?: ReactNode
  /** Short label for the collapsed view's chip. Falls back to `achTitle`. */
  achChip?: ReactNode
  /** Short label for the collapsed view's chip. Falls back to `netTermsTitle`. */
  netTermsChip?: ReactNode
  /** Short label for the collapsed view's chip. Falls back to `wireTitle`. */
  wireChip?: ReactNode
  /** Rendered in the collapsed view only; omit to render nothing. */
  collapsedNote?: ReactNode
  /** Callout rendered beneath the ACH Bank Transfer row's body; omit to render
   * nothing. Kept separate from `achDesc` so the row body stays one sentence. */
  achNote?: ReactNode
}

export type AvailablePaymentMethodsProps = {
  card?: boolean
  ach?: boolean
  netTerms?: boolean
  wire?: boolean
  /** The text to render for each section.
   * Recommended to use an intl component for each value.
   * `netTermsDesc` and `wireDesc` are typically composed with links or
   * modal triggers supplied by the consumer. */
  renderTextMap: AvailablePaymentMethodsRenderTextMap
  /** `default` variant only: when true, renders a chevron toggle that collapses
   * to a compact view (title, disclaimer, method chips); otherwise always
   * expanded with no toggle. The `card` variant ignores this — it is always
   * collapsible via native `<details>`. */
  collapsible?: boolean
  /** Initial expanded state when `collapsible` is true. Defaults to collapsed
   * (`false`). Ignored when `collapsible` is false. */
  defaultExpanded?: boolean
  children?: ReactNode
}

type PaymentMethod = {
  key: string
  icon: IconDefinition
  title: ReactNode
  desc: ReactNode
  chip: ReactNode
  note?: ReactNode
}

// Derives the enabled methods (in display order) with their icon, section text,
// and collapsed-view chip label. Shared by the default and card renderers so
// the enablement rules and chip fallbacks live in one place.
export const getPaymentMethods = ({
  card,
  ach,
  netTerms,
  wire,
  renderTextMap: t,
}: AvailablePaymentMethodsProps): PaymentMethod[] =>
  [
    card && {
      key: 'card',
      icon: faCreditCard,
      title: t.cardTitle,
      desc: t.cardDesc,
      chip: t.cardChip ?? t.cardTitle,
    },
    ach && {
      key: 'ach',
      icon: faMoneyCheckDollar,
      title: t.achTitle,
      desc: t.achDesc,
      chip: t.achChip ?? t.achTitle,
      note: t.achNote,
    },
    netTerms && {
      key: 'netTerms',
      icon: faClock,
      title: t.netTermsTitle,
      desc: t.netTermsDesc,
      chip: t.netTermsChip ?? t.netTermsTitle,
    },
    wire && {
      key: 'wire',
      icon: faArrowRightArrowLeft,
      title: t.wireTitle,
      desc: t.wireDesc,
      chip: t.wireChip ?? t.wireTitle,
    },
  ].filter(Boolean) as PaymentMethod[]
